<!-- NOTE: This is the global Search form. It appears in the header and on the global search results page -->
<template>
   <div class="search-wrapper" v-click-away="handleClickOutside">
      <FormGenerator
         id="search-form"
         ref="searchForm"
         :schema="searchSchema"
         :init-value="formInitValue"
         :on-update="true"
         @updated="handleFormUpdated"
         @focus="handleFormFocus"
         @keydown.arrow.down.prevent="navigateResults('down')"
         @keydown.arrow.up.prevent="navigateResults('up')"
         @keydown.enter.prevent="handleEnterKey"
      />
      <div
         v-if="shouldShowResults"
         ref="resultsContainer"
         class="search-results"
         data-lenis-prevent
      >
         <!-- "Search for:" link -->
         <div class="search-for">
            <NuxtLink
               ref="searchForLink"
               :to="{ name: 'search-results', query: { search: searchQuery } }"
               :class="{ hovered: selectedIndex === 0 }"
               @click="handleSearchForClick"
            >
               <span class="grey">Search for:</span> {{ searchQuery }}
            </NuxtLink>
         </div>

         <!-- Instant search results grouped by type -->
         <ul
            v-for="(group, groupIdx) in searchResults"
            :key="group.type || groupIdx"
         >
            <span class="label">{{ formatSearchLabel(group?.type) }}</span>
            <li
               v-for="(item, itemIdx) in group.hits"
               :key="item.slug || item.id || `${groupIdx}-${itemIdx}`"
            >
               <NuxtLink
                  :ref="(el) => setResultRef(el, groupIdx, itemIdx)"
                  :class="{
                     hovered:
                        selectedIndex === getResultIndex(groupIdx, itemIdx),
                  }"
                  :to="buildResultRoute(item, group.type)"
                  @click.passive="handleResultClick"
               >
                  <span v-html="item.hightlightedTitle"></span>
               </NuxtLink>
            </li>
         </ul>
      </div>
   </div>
</template>

<script setup lang="ts">
import { useDebugger } from "~/composables/useDebugger";

// ============================================================================
// Props & Composables
// ============================================================================

const props = defineProps({
   showInstantSearch: { type: Boolean, default: true },
   placeholder: { type: String, default: "Search titles, creators, topics" },
});

const route = useRoute();
const router = useRouter();
const { logInfo } = useDebugger();

const {
   search,
   searchResults,
   clearSearch,
   searchQuery: globalSearchQuery,
} = useGlobalSearch();

// ============================================================================
// Template Refs & Local State
// ============================================================================
import { clickAway as vClickAway } from "~/directives/clickAway.js";
const searchForm = useTemplateRef("searchForm");
const searchForLink = useTemplateRef("searchForLink");
const resultsContainer = ref(null);
const resultRefs = ref(new Map());

const focused = ref(false);
const selectedIndex = ref(0);
const queryCharLimit = ref(2);

// Sync local searchQuery with global state and route
const searchQuery = computed({
   get: () => globalSearchQuery.value ?? route.query?.search ?? "",
   set: (value) => {
      globalSearchQuery.value = value;
   },
});

// ============================================================================
// Form Schema
// ============================================================================

const searchSchema = reactive({
   fields: [
      {
         label: "",
         name: "search",
         placeholder: props.placeholder,
         as: "input",
         type: "search",
         autocomplete: "one-time-code",
      },
   ],
});

// ============================================================================
// Computed Properties
// ============================================================================

const formInitValue = computed(() => ({
   search: route.name === "search-results" ? searchQuery.value : "",
}));

const shouldShowResults = computed(
   () =>
      searchQuery.value &&
      focused.value &&
      props.showInstantSearch &&
      searchQuery.value.length > queryCharLimit.value,
);

const totalResults = computed(() => {
   if (!searchResults.value?.length) return 1; // Just "Search for" link
   return (
      searchResults.value.reduce(
         (total, group) => total + group.hits.length,
         0,
      ) + 1
   );
});

// ============================================================================
// Helper Functions
// ============================================================================

const setResultRef = (el, groupIdx, itemIdx) => {
   const dom = el?.$el ?? el;
   if (dom) {
      const index = getResultIndex(groupIdx, itemIdx);
      resultRefs.value.set(index, dom);
   }
};

const getResultIndex = (groupIdx, itemIdx) => {
   let index = 1; // Start at 1 (0 is "Search for" link)
   for (let i = 0; i < groupIdx; i++) {
      index += searchResults.value[i].hits.length;
   }
   return index + itemIdx;
};

const getAllFocusableElements = () => {
   const elements = [];

   // Add "Search for" link
   const searchForDom = searchForLink.value?.$el ?? searchForLink.value;
   if (searchForDom) elements.push(searchForDom);

   // Add all result links in order
   for (let i = 1; i < totalResults.value; i++) {
      const element = resultRefs.value.get(i);
      if (element) elements.push(element);
   }

   return elements;
};

const buildResultRoute = (item, groupType) => {
   if (groupType === "help_center_topics") {
      return {
         name: formatPageType(groupType),
         params: { slug: item?.slug },
      };
   }

   const baseParams = {
      discipline: item?.discipline?.slug || disciplineText(item.discipline_id),
      slug: item?.slug,
   };

   const resourceRoute = routeForResource({
      ...baseParams,
      type: item?.type,
      parentSlug: item?.core_work?.slug,
   });

   return resourceRoute ?? {
      name: formatPageType(groupType),
      params: baseParams,
   };
};

// ============================================================================
// Navigation Functions
// ============================================================================

const navigateResults = (direction) => {
   if (!props.showInstantSearch || !shouldShowResults.value) return;

   const increment = direction === "down" ? 1 : -1;
   const newIndex = Math.max(
      0,
      Math.min(totalResults.value - 1, selectedIndex.value + increment),
   );

   if (newIndex !== selectedIndex.value) {
      selectedIndex.value = newIndex;
      scrollToSelectedElement();
   }
};

const scrollToSelectedElement = () => {
   const elements = getAllFocusableElements();
   const el = elements[selectedIndex.value];

   el?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
   });
};

const handleEnterKey = async () => {
   const elements = getAllFocusableElements();
   const selectedElement = elements[selectedIndex.value];

   if (selectedElement) {
      const el = selectedElement?.$el ?? selectedElement;
      const url = new URL(el.href);

      // Close dropdown for result clicks (not "Search for:" link)
      if (selectedIndex.value > 0) {
         resetSearchForm();
         // Navigate with preserved query params
         await router.push({
            path: url.pathname,
         });
      } else {
         // Navigate with preserved query params
         await router.push({
            path: url.pathname,
            query: { search: searchQuery.value },
         });
      }
   } else {
      // Non-instant search fallback
      await router.push({
         query: {
            search: searchQuery.value,
         },
      });
      await search({ query: searchQuery.value, limit: 10 });
   }
};

// ============================================================================
// Event Handlers
// ============================================================================

const handleClickOutside = () => {
   focused.value = false;
};
const handleFormFocus = () => {
   focused.value = true;
};

const handleFormUpdated = async (formValues) => {
   if (formValues === undefined) return;
   if (searchQuery.value === formValues?.search) return;

   logInfo("Search form updated:", formValues?.search);
   searchQuery.value = formValues?.search;

   // Clear if empty
   if (!formValues?.search?.length) {
      clearSearch();
      return;
   }

   // Only trigger instant search if enabled and meets character limit
   if (
      props.showInstantSearch &&
      formValues.search.length > queryCharLimit.value
   ) {
      await search({ query: formValues.search });
   }
};

const handleSearchForClick = () => {
   logInfo("Navigating to full search results");
   focused.value = false;
};

const handleResultClick = () => {
   logInfo("Search result clicked");
   resetSearchForm();
};

const resetSearchForm = () => {
   // console.log("resetSearchForm called");

   const input = searchForm.value?.$el?.querySelector("input");
   if (input) {
      input.value = "";
      input.blur();
   }

   focused.value = false;
   selectedIndex.value = 0;
   searchQuery.value = "";
   clearSearch();
};

// ============================================================================
// Watchers & Side Effects
// ============================================================================

// Clear stored refs when results change to avoid stale DOM nodes
watch(
   () => searchResults.value,
   () => {
      resultRefs.value.clear();
      selectedIndex.value = 0;
   },
   { deep: true },
);

// Sync route query changes with search state
watch(
   () => route.query?.search,
   (newQuery) => {
      if (newQuery && newQuery !== searchQuery.value) {
         logInfo("Route query changed:", newQuery);
         searchQuery.value = newQuery;
      }
   },
);

// ============================================================================
// Lifecycle & Initialization
// ============================================================================

// Initialize search from URL on mount
await useAsyncData(`search-${route?.query?.search}`, async () => {
   if (route?.query?.search && searchResults.value.length === 0) {
      logInfo("Initializing search from URL:", route.query.search);
      await search({ query: route.query.search, limit: 10 });
   }
});
</script>

<style lang="scss">
@import url("~/assets/scss/globals/_search-form.scss");
</style>
